import _ from 'lodash';
import { VueGoodTable } from 'vue-good-table';

import { API, ENDPOINTS } from '@/app/services/api-url';
import { getItem, getDefaultItem, getLocalType } from '@/app/utils/toolkit/defaultItems';
import { getFormattedMemberName } from '@/app/utils/utils';
import { types } from '@/app/filters/filters';

export default {
  name: 'data-table',
  components: {
    VueGoodTable,
  },
  props: {
    hasPermission: {
      type: Boolean,
      default: true,
    },
    columns: {
      type: Array,
      default: () => [
        {
          label: 'Fund',
          field: 'fundName',
          filterOptions: {
            enabled: true,
            placeholder: 'Fund Name',
          },
        },
        {
          label: 'Status',
          field: 'status',
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterValue: 'ST|IN',
            filterDropdownItems: [
              { value: 'ST|IN', text: 'Active' },
              { value: 'STARTED', text: 'Started' },
              { value: 'IN_PROGRESS', text: 'In Progress' },
              { value: 'COMPLETE', text: 'Completed' },
            ],
            filterFn(data, filterString) {
              const hasComma = filterString.includes('|');
              if (hasComma) {
                const filters = filterString.split('|');
                const matched = filters.some(filter => data.includes(filter));
                return matched;
              }
              return data.includes(filterString);
            },
          },
        },
        {
          label: 'Last Updated',
          field: 'updatedDate',
          type: 'date',
          dateInputFormat: 'yyyy-MM-dd',
          dateOutputFormat: 'dd/MM/yyyy',
        },
        {
          label: 'Updated By',
          field: 'updatedByFormatted',
          filterOptions: {
            enabled: true,
            placeholder: 'Updated By',
          },
        },
        {
          label: 'Created At',
          field: 'createdDate',
          type: 'date',
          dateInputFormat: 'yyyy-MM-dd',
          dateOutputFormat: 'dd/MM/yyyy',
        },
        {
          label: 'Created By',
          field: 'createdByFormatted',
          filterOptions: {
            enabled: true,
            placeholder: 'Created By',
          },
        },
        {
          label: '',
          field: 'menu',
          sortable: false,
          width: '50px',
        },
      ],
    },
    rows: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    menuItems() {
      return (row) => {
        if (this.hasPermission) {
          if (row.status === 'IN_PROGRESS' && row.nextAction === 'PART_1') {
            return ['Share', 'Edit Fund', 'Edit Item', 'Schedule', 'Delete Item'];
          }
          if (row.status === 'COMPLETE') {
            return ['Share', 'View Item', 'Duplicate Item', 'Delete Item'];
          }
          if (row.status === 'STARTED') {
            return ['Edit Fund', 'Delete Item'];
          }
          return ['Share', 'Edit Fund', 'Edit Item', 'Delete Item'];
        }
        if (row.status === 'COMPLETE') {
          return ['View Item'];
        }
        return [];
      };
    },
  },
  methods: {
    async loadItem(row) {
      try {
        const accountUUID = this.$store.getters['account/getActiveAccount']
          .uuid;
        const response = await API.get(
          `${ENDPOINTS.GET_TOOLKIT_ITEM}/${row.id}?accountID=${accountUUID}&type=${row.type}`,
        );
        if (response.status === 200) {
          const item = response.data.data;
          await this.$store.dispatch('fund/loadFund', {
            uuid: item.uuid,
            status: item.status,
            cognitoID: item.cognitoID,
            relation: item.relation,
            ...item.fund,
          });

          const defaultItem = _.cloneDeep(getDefaultItem(getLocalType(item.type)));

          // Backwards capabilty for UUID in pensions
          if (item.type === 'PENSION_ESTABLISHMENT') {
            const fund = this.$store.getters['fund/getFund'];
            const pensionMember = item.data.requirements.member;
            const foundMember = fund.members
              .filter(fundMember => pensionMember.name === getFormattedMemberName(fundMember))[0];
            if (foundMember) {
              item.data.requirements.member.uuid = foundMember.uuid;
            }
          }

          await this.$store.dispatch('toolkit/loadItem', {
            uuid: item.uuid,
            type: item.type,
            status: item.status,
            accountID: item.relation,
            cognitoID: item.cognitoID,
            relation: item.relation,
            data: _.merge(defaultItem, item.data),
          });
          this.$mixpanel.track('Toolkit Item', {
            status: types(item.status),
            type: item.type,
            context: 'Dashboard',
            action: row.action,
            uuid: item.uuid,
          });
        }
      } catch (err) {
        console.log(err);
      }
    },
    handleAction(action, row) {
      switch (action) {
        case 'Share':
          this.handleShare({ ...row, action: 'share' });
          break;
        case 'Edit Item':
          this.handleEditItem({ ...row, action: 'edit item' });
          break;
        case 'Duplicate Item':
          this.handleDuplicateItem(row);
          break;
        case 'View Item':
          this.handleViewItem({ ...row, action: 'view item' });
          break;
        case 'Edit Fund':
          this.handleEditFund({ ...row, action: 'edit fund' });
          break;
        case 'Schedule':
          this.handleSchedule({ ...row, action: 'schedule' });
          break;
        case 'Delete Item':
          this.handleDeleteItem({ ...row, action: 'delete' });
          break;
        default:
          console.log('No Menu Action Found!');
      }
    },
    handleShare(row) {
      this.$emit('shareItem', row);
    },
    async handleEditFund(row) {
      await this.loadItem(row);
      const params = {};
      if (this.$store.getters['account/hasIntegrationTokens']) {
        params.step = 1;
      }
      this.$router.push({ name: 'fund-information', params }).catch(() => {
        /* Do Nothing */
      });
    },
    async handleEditItem(row) {
      await this.loadItem(row);
      this.$router.push({ name: getItem(getLocalType(row.type)).route }).catch(() => {
        /* Do Nothing */
      });
    },
    async handleViewItem(row) {
      await this.loadItem(row);
      this.$router.push({
        name: getItem(getLocalType(row.type)).route,
        params: { step: getItem(getLocalType(row.type)).reviewPage },
      }).catch(() => {
        /* Do Nothing */
      });
    },
    async handleSchedule(row) {
      if (row.type !== 'PENSION_ESTABLISHMENT') { return; }
      await this.loadItem(row);

      this.$router.push({
        name: getItem(getLocalType(row.type)).route,
        params: { step: 2 },
      }).catch(() => {
        /* Do Nothing */
      });
    },
    async handleDuplicateItem(row) {
      this.loadItem(row)
        .then(() => this.$store.dispatch('toolkit/duplicateItem'))
        .then(res => this.loadItem({
          id: res.data.data.uuid,
          type: res.data.data.type,
        }))
        .then(() => this.$router.push({ name: 'fund-information' }).catch(() => { /* Do Nothing */ }))
        .catch(err => console.log(err));
    },
    async handleDeleteItem(row) {
      this.$emit('deleteItem', row);
    },
  },
};
